import { FAIcon } from '../../../Icons/Icons';
import DropDown from '../../../DropDown/DropDown';
import type { DynamicObjectInterface, PaginationProps } from '../../types/propType';
import { scrollToTop } from '../Pagination';
import { useTranslation } from '../../../Translation/TranslationProvider';
import { KeyConstants } from '../../../types/enums';
import { calculatePaginationUpdate } from '../utils';
import styles from './DefaultPagination.module.scss';

const DefaultPagination = ({
  totalRecords,
  currentPage = 0,
  rowsPerPage = 5,
  updatePaginationData,
  startRecordIndex = () => 1,
  endRecordIndex,
  goToPreviousPage,
  goToNextPage,
  formatPaginationCallback,
  ariaLabelNextPage,
  ariaLabelPrevPage,
  pageSizeData,
  scrollToTopRefId,
}: PaginationProps) => {
  const t = useTranslation();

  const ariaLabelNextPageValue = ariaLabelNextPage || t('nextgen.NextPage.ariaLabel');
  const ariaLabelPrevPageValue = ariaLabelPrevPage || t('nextgen.PreviousPage.ariaLabel');
  const paginantionDefaultText = t('nextgen.Table.defaultPaginationText');

  const shouldHandleKeyDown = (e?: React.KeyboardEvent<HTMLDivElement>, callback?: () => void) => {
    if (callback && e && [KeyConstants.ENTER, KeyConstants.SPACE].includes(e.code as KeyConstants)) {
      callback();
    }
  };

  return (
    <div className={styles['pagination-container']}>
      {pageSizeData && (
        <div className={styles['pagination-rows']}>
          <span className={styles['pagination-rows-title']}>{paginantionDefaultText}:</span>
          <DropDown
            name="rows-per-page-dropdown"
            value={rowsPerPage}
            data={pageSizeData}
            isWithinTable={true}
            data-testid="dropdown-test-id"
            configData={{
              label: 'rows',
              onSelect: (e: DynamicObjectInterface) => {
                const updateData = calculatePaginationUpdate(
                  e.rows as number,
                  totalRecords,
                  currentPage,
                  startRecordIndex,
                  endRecordIndex,
                );
                updatePaginationData?.(updateData);
                scrollToTop(scrollToTopRefId);
              },
            }}
            disabledFilter
            isTablePaginationDropdown
          />
        </div>
      )}
      <div className={styles['pagination-record-range']}>
        {formatPaginationCallback ? (
          formatPaginationCallback?.({
            recordStartIndex: startRecordIndex?.(currentPage, rowsPerPage) + 1,
            recordEndIndex: endRecordIndex?.(currentPage, rowsPerPage) || rowsPerPage,
            totalRecords,
            currentPage: currentPage + 1,
            rowsPerPage,
          })
        ) : (
          <>
            {startRecordIndex?.(currentPage, rowsPerPage) + 1}-{endRecordIndex?.(currentPage, rowsPerPage)} of{' '}
            {totalRecords}
          </>
        )}
      </div>
      <div className={styles['pagination-next-prev-controls']}>
        <div
          role="button"
          aria-label={ariaLabelPrevPageValue}
          tabIndex={0}
          className={styles['icon-container']}
          data-testid="pagination-back-button"
          onClick={goToPreviousPage}
          onKeyDown={(e) => shouldHandleKeyDown(e, goToPreviousPage)}
        >
          <FAIcon iconName={'angle-left'} />
        </div>
        <div
          role="button"
          aria-label={ariaLabelNextPageValue}
          tabIndex={0}
          className={styles['icon-container']}
          data-testid="pagination-next-button"
          onClick={goToNextPage}
          onKeyDown={(e) => shouldHandleKeyDown(e, goToNextPage)}
        >
          <FAIcon iconName={'angle-right'} />
        </div>
      </div>
    </div>
  );
};

export default DefaultPagination;
